import {useSearchParams} from 'react-router-dom';
import {useCallback} from 'react';

/**
 * Hook réutilisable pour persister l'état d'un tableau dans l'URL (Search Params).
 *
 * @param {Object} defaults - Valeurs par défaut pour chaque clé d'état.
 *   Chaque valeur peut être : string, number, ou array (sera sérialisé en CSV).
 *
 * @returns {{ get, set, syncUrl }}
 *   - get(key) : valeur courante (typée selon le défaut)
 *   - set(key, value) : met à jour un paramètre et l'URL
 *   - syncUrl(patch) : met à jour plusieurs paramètres en une fois
 *
 * Exemple d'utilisation :
 *   const { get, syncUrl } = useTableSearchParams({
 *     tab: 0, page: 0, search: '', agences: [], typologies: []
 *   });
 *   const activeTab = get('tab');
 *   syncUrl({ tab: 1, page: 0 });
 */
function useTableSearchParams(defaults = {}) {
	const [searchParams, setSearchParams] = useSearchParams();

	const get = useCallback((key) => {
		const raw = searchParams.get(key);
		const def = defaults[key];
		if (raw === null) return def;
		if (Array.isArray(def)) return raw ? raw.split(',').filter(Boolean) : [];
		if (typeof def === 'number') return parseInt(raw, 10);
		return raw;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	const syncUrl = useCallback((patch) => {
		setSearchParams(prev => {
			const next = new URLSearchParams(prev);
			Object.entries(patch).forEach(([k, v]) => {
				const def = defaults[k];
				const isEmpty =
					v === null ||
					v === undefined ||
					v === '' ||
					(typeof def === 'number' && v === def) ||
					(Array.isArray(v) && v.length === 0);
				if (isEmpty) {
					next.delete(k);
				} else {
					next.set(k, Array.isArray(v) ? v.join(',') : String(v));
				}
			});
			return next;
		}, {replace: true});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setSearchParams]);

	const set = useCallback((key, value) => {
		syncUrl({[key]: value});
	}, [syncUrl]);

	return {get, set, syncUrl};
}

export default useTableSearchParams;
